import React, { useEffect, useState } from "react";
import type { Course, ProgressStats } from "@nelurio/contracts";
import { Card, EmptyState, PageHeader, StatCard } from "@nelurio/ui";
import { useI18n } from "@nelurio/i18n";
import { AiCoachCard } from "../components/AiCoachCard";
import { api, isDemoMode } from "../api/client";
import { useCourses } from "../hooks/useCourses";

/** The progress ENGINE view: one weighted score the student can actually move,
 *  with each component explained, plus per-course analytics (focus, trees,
 *  recall points → course progress %) over a selectable time window. */
interface AnalyticsComponent {
  key: "reviews" | "mastery" | "focus" | "tasks";
  weight: number;
  score: number;
  data: Record<string, number>;
}
interface AnalyticsCourse {
  courseId: string;
  title: string;
  color: string;
  focusMinutes: number;
  sessions: number;
  trees: number;
  treePoints: number;
  reviewPoints: number;
  masteryPct: number;
  progressPct: number;
}
interface AnalyticsOverview {
  from: string;
  to: string;
  days: number;
  score: number;
  components: AnalyticsComponent[];
  courses: AnalyticsCourse[];
}

const RANGES = [7, 30, 90] as const;

export function ProgressPage() {
  const { t, formatNumber, locale } = useI18n();
  const { courses } = useCourses();
  const [stats, setStats] = useState<ProgressStats | null>(null);
  const [days, setDays] = useState<7 | 30 | 90>(30);
  const [overview, setOverview] = useState<AnalyticsOverview | null>(null);

  useEffect(() => {
    if (isDemoMode()) return;
    api<ProgressStats>("/progress").then(setStats).catch(() => setStats(null));
  }, []);

  useEffect(() => {
    if (isDemoMode()) return;
    setOverview(null);
    api<AnalyticsOverview>(`/analytics/overview?days=${days}`)
      .then(setOverview)
      .catch(() => setOverview(null));
  }, [days]);

  // ECTS progress (degree math): modules planned per term vs modules earned
  // by COMPLETED terms — the concrete "how far into my degree am I" number.
  const [ects, setEcts] = useState<{ planned: number; earned: number } | null>(null);
  useEffect(() => {
    try {
      const semesters = JSON.parse(window.localStorage.getItem("nelurio.semesters.v1") ?? "[]") as Array<{ credits?: number; modules?: Array<{ credits?: number }>; status?: string }>;
      let planned = 0;
      let earned = 0;
      for (const semester of semesters) {
        const moduleCredits = (semester.modules ?? []).reduce((sum, module) => sum + (module.credits || 0), 0);
        const termCredits = moduleCredits || semester.credits || 0;
        planned += termCredits;
        if (semester.status === "completed") earned += termCredits;
      }
      setEcts({ planned, earned });
    } catch { setEcts(null); }
  }, []);

  const weeks = stats?.focus.weeks ?? [];
  const maxWeek = Math.max(1, ...weeks.map((week) => week.seconds));
  const weekLabels = weeks.map((week) =>
    new Intl.DateTimeFormat(locale === "en" ? "en-GB" : locale, { day: "numeric", month: "short" }).format(new Date(`${week.weekStart}T12:00:00`))
  );

  const compDetail = (component: AnalyticsComponent): string => {
    const d = component.data;
    switch (component.key) {
      case "reviews":
        return d.total === 0
          ? t("progress.comp.reviews.empty")
          : t("progress.comp.reviews.detail", d);
      case "mastery":
        return d.total === 0
          ? t("progress.comp.mastery.empty")
          : t("progress.comp.mastery.detail", d);
      case "focus":
        return t("progress.comp.focus.detail", d);
      case "tasks":
        return d.done + d.open === 0
          ? t("progress.comp.tasks.empty")
          : t("progress.comp.tasks.detail", d);
    }
  };

  const maxFocus = Math.max(1, ...(overview?.courses ?? []).map((course) => course.focusMinutes));

  return (
    <div className="page-stack rise">
      <PageHeader
        title={t("nav.progress")}
        subtitle={t("progress.subtitle")}
        actions={
          <div className="range-pills" role="tablist" aria-label={t("progress.rangeLabel")}>
            {RANGES.map((range) => (
              <button
                key={range}
                role="tab"
                aria-selected={days === range}
                className={`range-pills__btn ${days === range ? "is-active" : ""}`}
                onClick={() => setDays(range)}
              >
                {t(`progress.last${range}`)}
              </button>
            ))}
          </div>
        }
      />

      {/* Khoze weekly reflection — generated from real aggregates, live locale. */}
      <AiCoachCard kind="weekly" />

      {/* THE score: weighted, explained, movable. */}
      <Card className="score-engine tilt-card">
        <div className="section-heading compact">
          <div><span className="section-kicker">{t("progress.engineKicker")}</span><h2>{t("progress.scoreTitle")}</h2></div>
          <div className="score-ring" role="img" aria-label={`${formatNumber(Math.round(overview?.score ?? 0))}%`}>
            <strong>{formatNumber(Math.round(overview?.score ?? 0))}<small>%</small></strong>
          </div>
        </div>
        <div className="score-components">
          {(overview?.components ?? []).map((component) => (
            <div className="score-component" key={component.key}>
              <div className="score-component__head">
                <strong>{t(`progress.comp.${component.key}.title`)}</strong>
                <em>{formatNumber(Math.round(component.score))}% × {component.weight}%</em>
              </div>
              <i><b style={{ width: `${Math.max(2, Math.round(component.score))}%` }} /></i>
              <small>{compDetail(component)}</small>
            </div>
          ))}
          {!overview && <p className="tree-picker__hint">{t("common.loading")}</p>}
        </div>
        <p className="tree-picker__hint">{t("progress.scoreHow")}</p>
      </Card>

      {/* Per-course analytics: focus minutes + trees + recall → progress. */}
      <Card className="course-analytics tilt-card">
        <div className="section-heading compact">
          <div><span className="section-kicker">{t("progress.analyticsKicker")}</span><h2>{t("progress.focusByCourse")}</h2></div>
        </div>
        {overview?.courses.length ? (
          <div className="course-analytics__rows">
            {overview.courses.map((course) => (
              <div className="course-analytics__row" key={course.courseId}>
                <span className="course-analytics__dot" style={{ background: course.color }} />
                <div className="course-analytics__main">
                  <div className="course-analytics__top">
                    <strong>{course.title || t("progress.unassigned")}</strong>
                    <small>{formatNumber(course.focusMinutes)} {t("progress.minutesShort")} · {formatNumber(course.sessions)} ×</small>
                  </div>
                  <div className="course-analytics__bar"><i style={{ width: `${Math.max(3, Math.round((course.focusMinutes / maxFocus) * 100))}%`, background: course.color }} /></div>
                  <div className="course-analytics__meta">
                    <span>🌲 {formatNumber(course.trees)} {t("progress.trees")} · {formatNumber(course.treePoints)} {t("progress.treePoints")}</span>
                    <span>🧠 {formatNumber(course.reviewPoints)} {t("progress.reviewPoints")}</span>
                    <span className="course-analytics__progress">{t("progress.courseProgress")}: <b>{formatNumber(Math.round(course.progressPct))}%</b></span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <EmptyState icon="chart" title={t("progress.noFocusYet")} description={t("progress.startFocusHint")} />
        )}
      </Card>

      {ects && ects.planned > 0 && (
        <Card className="ects-card tilt-card">
          <div className="section-heading compact">
            <div><span className="section-kicker">{t("progress.ectsKicker")}</span><h2>{t("progress.ectsTitle")}</h2></div>
            <em className="ects-card__pct">{formatNumber(Math.round((ects.earned / ects.planned) * 100))}%</em>
          </div>
          <div className="ects-card__bar"><i style={{ width: `${Math.max(2, Math.round((ects.earned / ects.planned) * 100))}%` }} /></div>
          <p className="tree-picker__hint">
            {t("progress.ectsEarned", { earned: formatNumber(ects.earned), planned: formatNumber(ects.planned) })}
            {" — "}
            {t("progress.ectsHint")}
          </p>
        </Card>
      )}

      <div className="stats-grid">
        <StatCard label={t("progress.studyTime")} value={`${formatNumber(stats ? Math.round((stats.focus.totalSeconds / 3600) * 10) / 10 : 0)} ${t("progress.hours")}`} icon="timer" tone="purple" />
        <StatCard label={t("progress.focusSessions")} value={formatNumber(stats?.focus.sessions ?? 0)} icon="play" tone="green" />
        <StatCard label={t("dashboard.dueReviews")} value={formatNumber(stats?.reviews.due ?? 0)} icon="route" tone="blue" />
        <StatCard label={t("progress.masteredTopics")} value={`${formatNumber(stats?.topics.mastered ?? 0)} / ${formatNumber(stats?.topics.total ?? 0)}`} icon="chart" tone="amber" />
      </div>

      <div className="progress-grid">
        <Card className="progress-chart-card tilt-card">
          <div className="section-heading compact"><div><span className="section-kicker">{t("progress.activity")}</span><h2>{t("progress.focusWeeks")}</h2></div></div>
          {weeks.length ? (
            <div className="large-chart" role="img" aria-label={t("progress.focusWeeks")}>
              {weeks.map((week, index) => (
                <div className="large-chart__column" key={week.weekStart} style={{ "--delay": `${index * 60}ms` } as React.CSSProperties}>
                  <i style={{ height: `${Math.max(6, Math.round((week.seconds / maxWeek) * 100))}%` }} title={`${formatNumber(Math.round(week.seconds / 60))} ${t("common.minutes")}`} />
                  <small>{weekLabels[index]}</small>
                </div>
              ))}
            </div>
          ) : (
            <EmptyState icon="chart" title={t("progress.noFocusYet")} description={t("progress.startFocusHint")} />
          )}
        </Card>
        <Card className="mastery-list tilt-card">
          <div className="section-heading compact"><div><span className="section-kicker">{t("course.progress")}</span><h2>{t("progress.courseReadiness")}</h2></div></div>
          {courses.length ? courses.map((course: Course) => (
            <div className="mastery-row" key={course.id}>
              <span style={{ background: course.color }}>{course.icon}</span>
              <div>
                <strong>{course.title}</strong>
                <i><b style={{ width: `${course.progress}%`, background: course.color }} /></i>
              </div>
              <em>{formatNumber(course.progress)}%</em>
            </div>
          )) : <EmptyState icon="book" title={t("courses.emptyTitle")} description={t("courses.emptyBody")} />}
        </Card>
      </div>
    </div>
  );
}
